import { createHighlighter } from "shiki";
import { createScopedLogger } from "./logger";

const logger = createScopedLogger("ShikiHighlighter");

// Global highlighter instance
let highlighterInstance = null;
let isInitializing = false;

// Language normalization map
const LANGUAGE_MAP = {
  js: "javascript",
  ts: "typescript",
  jsx: "javascript",
  tsx: "typescript",
  py: "python",
  rb: "ruby",
  sh: "bash",
  yml: "yaml",
  md: "markdown",
  vue: "vue",
  svelte: "svelte",
  astro: "astro",
};

// Core languages to always load
const CORE_LANGUAGES = [
  "javascript",
  "typescript",
  "python",
  "java",
  "cpp",
  "c",
  "csharp",
  "go",
  "rust",
  "php",
  "ruby",
  "swift",
  "kotlin",
  "scala",
  "html",
  "css",
  "scss",
  "json",
  "xml",
  "yaml",
  "markdown",
  "bash",
  "sql",
  "dockerfile",
  "vue",
  "svelte",
  "astro",
  "jsx",
  "tsx",
  "plaintext",
];

// Custom theme configuration that matches the design system
const CUSTOM_THEME = {
  name: "artifact-dark",
  type: "dark",
  colors: {
    "editor.background": "#0d1117",
    "editor.foreground": "#e6edf3",
  },
  tokenColors: [
    {
      scope: ["comment", "punctuation.definition.comment"],
      settings: {
        foreground: "var(--ds-gray-900)",
        fontStyle: "italic",
      },
    },
    {
      scope: [
        "constant",
        "constant.numeric",
        "constant.language",
        "constant.character",
        "constant.other",
        "keyword.other.unit",
        "support.constant",
      ],
      settings: {
        foreground: "var(--ds-blue-900)",
      },
    },
    {
      scope: [
        "string",
        "string.quoted",
        "string.template",
        "string.unquoted",
        "string.regexp",
      ],
      settings: {
        foreground: "var(--ds-green-900)",
      },
    },
    {
      scope: [
        "keyword",
        "keyword.control",
        "keyword.operator",
        "keyword.other",
        "storage.type",
        "storage.modifier",
      ],
      settings: {
        foreground: "var(--ds-pink-900)",
      },
    },
    {
      scope: [
        "variable",
        "variable.parameter",
        "variable.other",
        "meta.function-call.arguments",
      ],
      settings: {
        foreground: "var(--ds-amber-900)",
      },
    },
    {
      scope: [
        "entity.name.function",
        "meta.function-call",
        "support.function",
        "entity.name.method",
      ],
      settings: {
        foreground: "var(--ds-purple-900)",
      },
    },
    {
      scope: [
        "punctuation",
        "punctuation.definition",
        "punctuation.separator",
        "punctuation.terminator",
        "meta.brace",
        "meta.bracket",
      ],
      settings: {
        foreground: "var(--ds-gray-1000)",
      },
    },
    {
      scope: ["markup.underline.link", "string.other.link"],
      settings: {
        foreground: "var(--ds-green-900)",
      },
    },
  ],
};

// Theme config for different modes
const THEME_CONFIG = {
  light: "artifact-dark", // Use our custom theme for all modes
  dark: "artifact-dark",
  auto: "artifact-dark",
};

/**
 * Initialize the highlighter with all necessary languages and themes
 * This runs immediately when the module loads
 */
async function initializeHighlighter() {
  if (highlighterInstance || isInitializing) {
    return highlighterInstance;
  }

  isInitializing = true;

  try {
    highlighterInstance = await createHighlighter({
      themes: ["github-dark", "github-light"],
      langs: CORE_LANGUAGES,
    });

    logger.info("Shiki highlighter initialized successfully");
    return highlighterInstance;
  } catch (error) {
    logger.error("Failed to initialize Shiki highlighter:", error);
    highlighterInstance = null;
    return null;
  } finally {
    isInitializing = false;
  }
}

/**
 * Normalize language identifier
 */
function normalizeLanguage(lang) {
  if (!lang) return "plaintext";

  const lower = lang.toLowerCase();

  // Handle plain text cases
  if (lower === "text" || lower === "plain" || lower === "txt") {
    return "plaintext";
  }

  const normalized = LANGUAGE_MAP[lower] || lower;
  return normalized;
}

/**
 * Synchronous code highlighting with custom theme variables
 * This is the key function for streaming without flashing
 */
export function highlightCodeSync(
  code,
  language = "javascript",
  theme = "auto"
) {
  // If highlighter isn't ready, return fallback HTML immediately
  if (!highlighterInstance) {
    return createFallbackHTML(code, language);
  }

  try {
    const normalizedLang = normalizeLanguage(language);

    // Check if language is loaded
    const loadedLanguages = highlighterInstance.getLoadedLanguages();

    // For plaintext, just use fallback (no syntax highlighting needed)
    if (normalizedLang === "plaintext") {
      return createFallbackHTML(code, language);
    }

    const langToUse = loadedLanguages.includes(normalizedLang)
      ? normalizedLang
      : "javascript"; // Default to javascript instead of text

    // Use github-dark as base theme but we'll override with CSS variables
    const html = highlighterInstance.codeToHtml(code, {
      lang: langToUse,
      theme: "github-dark",
    });

    // Apply custom CSS variables to the generated HTML
    return applyCustomTheme(html);
  } catch (error) {
    logger.warn("Shiki highlighting failed, using fallback:", error);
    return createFallbackHTML(code, language);
  }
}

/**
 * Apply custom theme variables to Shiki-generated HTML
 */
function applyCustomTheme(html) {
  // Replace the default theme classes and add our custom variables
  let customHtml = html
    .replace(
      '<pre class="shiki github-dark"',
      '<pre class="shiki github-dark artifact-theme"'
    )
    .replace(
      'style="background-color:#0d1117;color:#e6edf3"',
      'style="background-color:var(--ds-background-100);color:var(--ds-gray-1000);padding:var(--padding,20px) 0;margin:0;overflow-x:auto;counter-reset:line"'
    );

  // Map specific GitHub Dark colors to our design system variables
  const colorMappings = {
    "#79c0ff": "var(--ds-blue-900)", // Constants/numbers -> bright cyan
    "#a5d6ff": "var(--ds-green-900)", // Strings -> bright green
    "#7d8590": "var(--ds-gray-900)", // Comments -> muted gray
    "#ff7b72": "var(--ds-pink-900)", // Keywords -> bright pink
    "#ffa657": "var(--ds-amber-900)", // Parameters -> bright orange
    "#d2a8ff": "var(--ds-purple-900)", // Functions -> bright purple
    "#e6edf3": "var(--ds-gray-1000)", // Default text/punctuation -> light gray
    // Also map some additional common colors
    "#f85149": "var(--ds-pink-900)", // Alternative red/pink
    "#7ee787": "var(--ds-green-900)", // Alternative green
    "#58a6ff": "var(--ds-blue-900)", // Alternative blue
    "#ffa198": "var(--ds-amber-900)", // Alternative orange
  };

  // Apply color mappings to inline styles
  Object.entries(colorMappings).forEach(([originalColor, customVar]) => {
    const regex = new RegExp(`color:${originalColor.replace("#", "#")}`, "g");
    customHtml = customHtml.replace(regex, `color:${customVar}`);
  });

  return customHtml;
}

/**
 * Create fallback HTML with custom theme styling when Shiki isn't available
 */
function createFallbackHTML(code, language) {
  const escapedCode = code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  return `
    <pre class="shiki github-dark artifact-theme" style="background-color: var(--ds-background-100, #0d1117); color: var(--ds-gray-1000, #e6edf3); padding: var(--padding, 20px) 0; margin: 0; overflow-x: auto; counter-reset: line; font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace; font-size: 14px; line-height: 1.45;" tabindex="0">
      <code>${escapedCode}</code>
    </pre>
  `;
}

/**
 * Get available languages
 */
export function getAvailableLanguages() {
  return highlighterInstance
    ? highlighterInstance.getLoadedLanguages()
    : CORE_LANGUAGES;
}

/**
 * Get available themes
 */
export function getAvailableThemes() {
  return highlighterInstance
    ? highlighterInstance.getLoadedThemes()
    : ["github-dark"];
}

/**
 * Check if highlighter is ready
 */
export function isHighlighterReady() {
  return !!highlighterInstance;
}

// Start initialization immediately when module loads
initializeHighlighter().catch((error) => {
  logger.error("Failed to initialize Shiki on module load:", error);
});

// Export the sync function as default
export default highlightCodeSync;
